/* Presentation decisions for the audit chain, kept out of the component.
 *
 * Rebuilt from the Claude Design artboard, which draws **four columns — Time, Actor, Action, Hash**
 * — and nothing else. The six-column table this replaced carried a chain-position column and a
 * subject-type column with the aggregate id behind a hover glyph; neither is in the reference, so
 * both are gone and the subject is folded into the action cell the way the artboard renders it.
 *
 * One rule still shapes all of it: **this screen shows metadata about actions, never what changed.**
 * The route does not return `payload` — it is not selected by the statement, so no snapshot leaves
 * PostgreSQL — and `meta.payload_omitted` is on every response. The reference states that in its own
 * words, which is why the band's copy is the artboard's rather than ours.
 */

import type { AuditEntry } from '@/shared/api/generated/audit';

/** How much of a digest the reference prints.
 *
 * **Thirteen, and with no ellipsis.** The artboard renders `9f31c2ab4e7d1` — thirteen hex
 * characters, ending flush. The previous implementation printed twelve followed by `…`, which is a
 * different width and a different shape; the full value stays on the `title`, which is the
 * artboard's own device and the reason truncating costs an auditor nothing.
 *
 * **Truncated for display only.** Nothing here derives meaning from the prefix — a hash is compared
 * whole or not at all, and a screen that filtered on thirteen characters would be claiming a
 * collision cannot happen.
 */
export const HASH_PREFIX = 13;

export function shortHash(value: string): string {
  return value.slice(0, HASH_PREFIX);
}

/** How an actor is rendered.
 *
 * A `system:` prefix means an unattended sweep rather than a person, and the reference gives the two
 * different treatments: a person at weight 500 in the body face, a sweep in mono and muted. "Who did
 * this" has a different answer and a different follow-up for each, and rendering them alike would
 * make an investigation start in the wrong place. The platform writes `system:autonomous-sweep` and
 * thirteen siblings deliberately so the distinction exists to be drawn.
 */
export const SYSTEM_ACTOR_PREFIX = 'system:';

export function isSystemActor(actor: string): boolean {
  return actor.startsWith(SYSTEM_ACTOR_PREFIX);
}

/** The four columns the reference draws, in its order, as a data declaration.
 *
 * Declared rather than written inline so the header and the body cannot drift about which column is
 * which. `hash` is the only right-aligned one, which the artboard sets on the `th` as well as the
 * `td` — a right-aligned column whose header stayed left would read as a mistake.
 */
export const COLUMNS = ['when', 'actor', 'action', 'hash'] as const;

/** A stable React key for one entry.
 *
 * `(chain_date, sequence)` is the row's real primary key within a tenant, so it is unique by
 * construction — and it is what the keyset cursor pages on, so two rows on the same page cannot
 * share one. `sequence` alone would not do: it restarts at 1 each chain date, so two rows on
 * adjacent days share a value. Joined with `#` rather than `.`, because `.` is the character most
 * likely to appear inside a value.
 */
export function entryKey(entry: AuditEntry): string {
  return `${entry.chain_date}#${String(entry.sequence)}`;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

function clock(at: Date): string {
  return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
}

function daysApart(later: Date, earlier: Date): number {
  const a = Date.UTC(later.getFullYear(), later.getMonth(), later.getDate());
  const b = Date.UTC(earlier.getFullYear(), earlier.getMonth(), earlier.getDate());
  return Math.round((a - b) / 86_400_000);
}

/** The Time cell, in the reference's own shape.
 *
 * The artboard renders `Today 09:12` and `Yesterday 17:02` — a named day plus a 24-hour clock, set
 * in tabular figures so a column of them scans vertically. **The two examples it gives are both
 * inside two days, so the older form is the one place this screen extends the reference**, and it
 * extends it as narrowly as possible: the same clock with a day and month in front, and a year only
 * when the entry is not from this one. Nothing else is invented.
 *
 * `now` is a parameter for the reason every relative time in this app takes one: a component that
 * reads the clock itself re-renders with a different answer on every unrelated state change.
 *
 * The absolute instant is never lost — `absoluteInstant` puts it on the cell's `title`, which is the
 * artboard's device for the hash and is what keeps a relative label safe on a screen where somebody
 * quotes timestamps.
 */
export function whenLabel(occurredAt: string, now: Date): string {
  const at = new Date(occurredAt);
  if (Number.isNaN(at.getTime())) return occurredAt;

  const apart = daysApart(now, at);
  if (apart === 0) return `Today ${clock(at)}`;
  if (apart === 1) return `Yesterday ${clock(at)}`;

  const month = MONTHS[at.getMonth()] ?? '';
  const day = String(at.getDate()).padStart(2, '0');
  return at.getFullYear() === now.getFullYear()
    ? `${day} ${month} ${clock(at)}`
    : `${day} ${month} ${String(at.getFullYear())} ${clock(at)}`;
}

/** The full instant, for the cell's `title`. Locale-formatted, because the platform does not know
 * the reader's timezone and the browser does. */
export function absoluteInstant(occurredAt: string): string {
  const at = new Date(occurredAt);
  return Number.isNaN(at.getTime()) ? occurredAt : at.toLocaleString();
}

/** The Action cell: what was done, then what it was done to.
 *
 * The artboard renders `incident.acknowledge · #241` — an action and a subject label either side of
 * a middle dot. **`aggregate_type` is the subject the API actually carries.** The artboard's second
 * segment is a human name (`dwh-snowflake`, `run-8839`), and no route resolves an `aggregate_id` to
 * one: there is no name lookup spanning the sixty aggregate types the chain records, so filling that
 * segment with a name would mean inventing one. The alternative — a truncated uuid — is the bare-id
 * defect this codebase has shipped four times, and it would put an unreadable value in the cell a
 * reader scans second.
 *
 * So the segment carries the type, which is real, always present, and is what the segment *means*.
 * The id is not dropped: it goes on the cell's `title`, the artboard's own device, so an auditor
 * tracing one aggregate can still take it.
 */
export function actionLabel(entry: AuditEntry): string {
  return `${entry.action} · ${entry.aggregate_type}`;
}

/* ---------------------------------------------------------------- the time range */

/** The periods an auditor narrows to. `all` is the unfiltered trail.
 *
 * Six, and closed: every one maps onto a window the API already takes. There is no custom
 * from/to picker here because the reference draws no filter at all and this is the smallest
 * addition that answers "what happened this month" — a picker is a bigger surface and a
 * different question.
 */
export type RangeId = 'all' | 'today' | 'yesterday' | 'week' | 'month' | 'year';

export const RANGE_IDS: readonly RangeId[] = [
  'all',
  'today',
  'yesterday',
  'week',
  'month',
  'year',
];

/** The API's half-open window for a period, or an empty object for the whole trail.
 *
 * **Half-open — `from` inclusive, `to` exclusive — because that is what the route takes**, and it
 * is why consecutive periods tile: an entry on a boundary belongs to exactly one of them. The route
 * also refuses `occurred_to <= occurred_from`, which no window here can produce.
 *
 * **The upper bound is the start of the *next* period, never `now`.** Bounding at the current
 * instant would drop an entry written between the render and the request — on the one screen whose
 * subject is completeness — and it would leave a gap between "today" and "yesterday" rather than
 * two windows that meet.
 *
 * **Local midnight, not UTC.** A reader asking for "today" means their day; the chain stores
 * instants and the browser knows the offset, so the boundaries are computed locally and sent as
 * instants. A UTC day would put an early-morning entry in Mumbai under yesterday.
 *
 * **The week starts on Monday** (ISO-8601, and the convention in this platform's market), so "this
 * week" on a Sunday still covers the six days behind it rather than only that day.
 */
export function windowFor(
  range: RangeId,
  now: Date,
): { readonly occurredFrom?: string; readonly occurredTo?: string } {
  if (range === 'all') return {};

  const midnight = (year: number, month: number, day: number): Date =>
    new Date(year, month, day, 0, 0, 0, 0);

  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();

  let from: Date;
  let to: Date;
  switch (range) {
    case 'today':
      from = midnight(y, m, d);
      to = midnight(y, m, d + 1);
      break;
    case 'yesterday':
      from = midnight(y, m, d - 1);
      to = midnight(y, m, d);
      break;
    case 'week': {
      // `getDay()` is 0 for Sunday, so this is the offset back to Monday.
      const sinceMonday = (now.getDay() + 6) % 7;
      from = midnight(y, m, d - sinceMonday);
      to = midnight(y, m, d - sinceMonday + 7);
      break;
    }
    case 'month':
      from = midnight(y, m, 1);
      to = midnight(y, m + 1, 1);
      break;
    case 'year':
      from = midnight(y, 0, 1);
      to = midnight(y + 1, 0, 1);
      break;
  }

  return { occurredFrom: from.toISOString(), occurredTo: to.toISOString() };
}
